import { useQueryClient } from "@tanstack/react-query";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";

import { useExploreSections, useMedia } from "@/hooks";
import type { ExploreSection, ExploreSubSectionType } from "@/types";
import { Button, ControlledDropzone, errorToast, Input } from "@/ui";
import { getFileExtension } from "@/utils";

// Form for adding a new article/subsection to an Explore section
// Admins enter the article name, URL (to Oxford Mindfulness website), and optional image
interface FormValues {
  name: string;
  url: string;
  image?: File;
}

interface Props {
  sectionId: string;
  onClose: () => void;
}

export const AddExploreSubSectionForm = ({ sectionId, onClose }: Props) => {
  const { useCreateExploreSubSection } = useExploreSections();
  const { useUpload } = useMedia();
  const { mutateAsync: createSubSection, isPending } =
    useCreateExploreSubSection();
  const { upload, isUploading } = useUpload();
  const queryClient = useQueryClient();

  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
    control,
  } = useForm<FormValues>({
    mode: "all",
    defaultValues: {
      name: "",
      url: "",
    },
  });

  const invalidateAndClose = () => {
    void queryClient.invalidateQueries({ queryKey: ["exploreSections"] });
    onClose();
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      // Get current section to calculate order
      const section = queryClient.getQueryData<ExploreSection>([
        "exploreSections",
        sectionId,
      ]);
      const order = section?.subSections ? section.subSections.length : 0;

      const subSectionData: {
        explore_section_id: string;
        name: string;
        url: string;
        type: ExploreSubSectionType;
        order: number;
        image_extension?: string;
      } = {
        explore_section_id: sectionId,
        name: data.name,
        url: data.url,
        type: "article", // Type is always "article" for blog/research posts
        order,
      };

      // Add image extension if file is uploaded
      if (data.image) {
        subSectionData.image_extension =
          getFileExtension(data.image.name) ?? undefined;
      }

      const newSubSection = await createSubSection(subSectionData);

      // Upload image if provided and wait for completion
      if (data.image && newSubSection.id) {
        const result = await upload(
          data.image,
          `/explore/sub-sections/${newSubSection.id}/image/upload-url`,
        );
        if (!result.success) {
          errorToast("Failed to upload image. Please try again.");
          return; // Keep modal open on upload failure
        }
      }

      invalidateAndClose();
    } catch (error) {
      errorToast(error);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex w-full max-w-[540px] flex-col gap-y-8"
    >
      <div className="flex flex-col gap-y-8">
        <Input
          id="name"
          label="Article Title"
          placeholder="e.g., Research Paper on Mindfulness"
          {...register("name", { required: "Article title is required" })}
          error={errors.name?.message}
          required
        />

        <Input
          id="url"
          label="Article URL"
          placeholder="https://oxfordmindfulness.org/..."
          {...register("url", {
            required: "URL is required",
            pattern: {
              value: /^https?:\/\/.+/,
              message:
                "Please enter a valid URL starting with http:// or https://",
            },
          })}
          error={errors.url?.message}
          required
        />

        <ControlledDropzone
          name="image"
          control={control}
          label="Article Image (Optional)"
          accept={{ "image/*": [".png", ".jpg", ".jpeg"] }}
        />
      </div>

      <div className="flex justify-center gap-2">
        <Button type="button" variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={!isValid || isPending || isUploading}>
          {isPending || isUploading ? "Creating..." : "Create Article"}
        </Button>
      </div>
    </form>
  );
};
